import React, { useState } from 'react';
import { Lock, Unlock } from 'lucide-react';

/**
 * SimpleCollegeProgress - Clean, minimal progress tracking for college selection
 * Shows simple "X of Y" count with subtle lock functionality
 */
const SimpleCollegeProgress = ({ 
  categoryKey, 
  currentCount = 0, 
  targetCount = 4,
  isLocked = false,
  onToggleLock,
  className = ''
}) => {
  const [showLockTooltip, setShowLockTooltip] = useState(false);

  // Determine status for styling
  const getStatus = () => {
    if (isLocked) return 'locked';
    if (currentCount >= targetCount) return 'complete';
    if (currentCount > 0) return 'progress';
    return 'empty';
  };

  const status = getStatus();

  // Get pill styling based on status
  const getPillStyle = () => {
    // slate-900 when at target count, gray when under or over
    if (currentCount === targetCount) {
      return 'bg-slate-900 text-white';
    } else {
      return 'bg-stone-400 text-white';
    }
  };

  const handleLockClick = (e) => {
    e.stopPropagation();
    if (onToggleLock) {
      // Allow unlocking anytime, but only allow locking when at target
      if (isLocked || currentCount >= targetCount) {
        onToggleLock(categoryKey, !isLocked);
      }
    }
  };

  const canToggleLock = isLocked || currentCount >= targetCount; // Must reach target to lock, or can always unlock

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Count Display */}
      <div className={`px-3 py-1 rounded-full text-sm font-medium ${getPillStyle()}`}>
        {currentCount} of {targetCount}
      </div>

      {/* Lock Icon - Only show if can be locked or is already locked */}
      {(canToggleLock || isLocked) && (
        <button
          onClick={handleLockClick}
          onMouseEnter={() => setShowLockTooltip(true)}
          onMouseLeave={() => setShowLockTooltip(false)}
          className={`relative p-1.5 rounded-lg transition-colors ${
            isLocked
              ? 'text-azure-600 hover:bg-azure-50'
              : 'text-stone-400 hover:text-slate-600 hover:bg-stone-50'
          }`}
          title={isLocked ? 'Selection locked - click to unlock' : 'Lock this selection at target count'}
        >
          {isLocked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
          
          {/* Simple Tooltip */}
          {showLockTooltip && (
            <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-slate-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
              {isLocked ? 'Locked' : 'Lock'}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
            </div>
          )}
        </button>
      )}
    </div>
  );
};

export default SimpleCollegeProgress;